import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import {
	FiBell,
	FiArrowLeft,
	FiRefreshCw,
	FiTrash2,
	FiEye,
	FiChevronLeft,
	FiChevronRight,
} from 'react-icons/fi';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import { notificationEvents } from '../../utils/notificationEvents';
import { BASE_URL } from '../../config';

const NotificationsPage = () => {
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const history = useHistory();
	const token = useSelector((state) => state.admin.token);

	const ITEMS_PER_PAGE = 10;

	const fetchAllNotifications = async (page = 1) => {
		try {
			setLoading(true);
			setError('');

			const offset = (page - 1) * ITEMS_PER_PAGE;
			const response = await fetch(
				`${BASE_URL}/api/admin/forms/notifications/events?limit=${ITEMS_PER_PAGE}&offset=${offset}&all=true`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.ok) {
				const data = await response.json();
				setNotifications(data.events || []);
				setTotalCount(data.total || 0);
				setTotalPages(Math.ceil((data.total || 0) / ITEMS_PER_PAGE));
				setCurrentPage(page);
			} else {
				setError('Failed to load notifications');
			}
		} catch (error) {
			console.error('Error fetching notifications:', error);
			setError('Error loading notifications');
		} finally {
			setLoading(false);
		}
	};

	const markAllAsSeen = async () => {
		try {
			// Add confirmation for large numbers of notifications
			const unseenCount = notifications.filter((n) => !n.viewed_at).length;
			if (unseenCount > 10) {
				const confirmed = window.confirm(
					`Mark all ${totalCount} notifications as seen? This cannot be undone.`
				);
				if (!confirmed) return;
			}

			await fetch(`${BASE_URL}/api/admin/forms/notifications/events/mark-all-seen`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			// Update local state immediately to show all notifications as seen
			const currentTime = new Date().toISOString();
			setNotifications((prev) =>
				prev.map((notification) => ({
					...notification,
					viewed_at: notification.viewed_at || currentTime,
				}))
			);

			// Notify other components (like NotificationBell)
			notificationEvents.markAllAsSeen();

			// Refresh the current page to get updated data from server
		} catch (error) {
			console.error('Error marking all notifications as seen:', error);
			// Show user-friendly error message
			alert('Failed to mark notifications as seen. Please try again.');
		}
	};

	const markNotificationAsSeen = async (notificationId) => {
		try {
			await fetch(
				`${BASE_URL}/api/admin/forms/notifications/events/${notificationId}/mark-seen`,
				{
					method: 'POST',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			// Update local state to reflect the change immediately
			setNotifications((prev) =>
				prev.map((notification) =>
					notification.id === notificationId
						? { ...notification, viewed_at: new Date().toISOString() }
						: notification
				)
			);

			// Notify other components (like NotificationBell)
			notificationEvents.markAsSeen(notificationId);
		} catch (error) {
			console.error('Error marking notification as seen:', error);
		}
	};

	useEffect(() => {
		if (token) {
			fetchAllNotifications(1);
		}
	}, [token]);

	// Listen for notification events from other components (like NotificationBell)
	useEffect(() => {
		const handleMarkAllAsSeen = () => {
			// Update local state to show all notifications as seen
			const currentTime = new Date().toISOString();
			setNotifications((prev) =>
				prev.map((notification) => ({
					...notification,
					viewed_at: notification.viewed_at || currentTime,
				}))
			);
		};

		const handleMarkAsSeen = (event) => {
			const { notificationId } = event.detail;
			// Update local state to show this notification as seen
			setNotifications((prev) =>
				prev.map((notification) =>
					notification.id === notificationId
						? { ...notification, viewed_at: new Date().toISOString() }
						: notification
				)
			);
		};

		// Add event listeners
		notificationEvents.addEventListener(
			notificationEvents.MARK_ALL_AS_SEEN,
			handleMarkAllAsSeen
		);
		notificationEvents.addEventListener(
			notificationEvents.MARK_AS_SEEN,
			handleMarkAsSeen
		);

		// Cleanup event listeners
		return () => {
			notificationEvents.removeEventListener(
				notificationEvents.MARK_ALL_AS_SEEN,
				handleMarkAllAsSeen
			);
			notificationEvents.removeEventListener(
				notificationEvents.MARK_AS_SEEN,
				handleMarkAsSeen
			);
		};
	}, []);

	const handleNotificationClick = async (notification) => {
		// Mark this specific notification as seen first
		if (!notification.viewed_at) {
			await markNotificationAsSeen(notification.id);
		}

		// Check if this notification has a valid submission to navigate to
		if (
			notification.submission_id &&
			notification.form_name &&
			notification.first_name
		) {
			// This is a real notification with valid submission data
			history.push(
				`/admin/submissions/${notification.submission_id}?from=notifications`
			);
		} else if (notification.metadata?.test_event) {
			// This is a test notification - navigate to submissions list
			history.push('/admin/submissions?from=notifications');
		} else {
			// This is a notification for a deleted/missing submission
			// Show the metadata information or navigate to submissions list
			if (notification.metadata?.form_name) {
				// Navigate to submissions filtered by form if we have that info
				history.push(
					`/admin/submissions?search=${encodeURIComponent(
						notification.metadata.form_name
					)}&from=notifications`
				);
			} else {
				// Just go to submissions list
				history.push('/admin/submissions?from=notifications');
			}
		}
	};

	const handleRefresh = () => {
		fetchAllNotifications(currentPage);
	};

	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= totalPages) {
			fetchAllNotifications(newPage);
		}
	};

	const formatNotificationTime = (createdAt) => {
		try {
			const date = new Date(createdAt);
			return {
				relative: formatDistanceToNow(date, { addSuffix: true }),
				absolute: date.toLocaleDateString('en-US', {
					year: 'numeric',
					month: 'short',
					day: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
				}),
			};
		} catch (error) {
			return { relative: 'Recently', absolute: 'Unknown time' };
		}
	};

	if (loading && currentPage === 1) {
		return (
			<div className='container mx-auto px-4 py-8'>
				<div className='flex justify-center items-center h-64'>
					<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900'></div>
				</div>
			</div>
		);
	}

	return (
		<div className='container mx-auto px-4 py-8'>
			{/* Header */}
			<div className='flex items-center justify-between mb-6'>
				<div className='flex items-center space-x-4'>
					<Button
						variant='outline'
						onClick={() => history.goBack()}
						className='flex items-center'>
						<FiArrowLeft className='h-4 w-4 mr-2' />
						Back
					</Button>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>
							All Notifications
						</h1>
						<p className='text-sm text-gray-500 mt-1'>
							View all notification events and activity ({totalCount} total)
						</p>
					</div>
				</div>

				<div className='flex items-center space-x-2'>
					<Button
						variant='outline'
						onClick={handleRefresh}
						disabled={loading}
						className='flex items-center'>
						<FiRefreshCw
							className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
						/>
						Refresh
					</Button>

					{totalCount > 0 && (
						<Button
							variant='outline'
							onClick={markAllAsSeen}
							className='flex items-center'>
							<FiEye className='h-4 w-4 mr-2' />
							Mark All as Seen
						</Button>
					)}
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6'>
					<p className='font-bold'>Error:</p>
					<p>{error}</p>
				</div>
			)}

			{/* Notifications List */}
			<Card>
				<Card.Header>
					<div className='flex items-center justify-between'>
						<h2 className='text-lg font-medium'>
							Notification Events
							{totalCount > 0 && (
								<span className='text-sm font-normal text-gray-500 ml-2'>
									(Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
									{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of{' '}
									{totalCount} notifications)
								</span>
							)}
						</h2>
						{loading && (
							<div className='animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-900'></div>
						)}
					</div>
				</Card.Header>

				<Card.Content>
					{notifications.length === 0 ? (
						<div className='text-center py-12'>
							<FiBell className='h-12 w-12 text-gray-400 mx-auto mb-4' />
							<h3 className='text-lg font-medium text-gray-900 mb-2'>
								No notifications found
							</h3>
							<p className='text-gray-500'>
								Notification events will appear here when new submissions are
								received.
							</p>
						</div>
					) : (
						<>
							<div className='space-y-4'>
								{notifications.map((notification) => {
									const timeInfo = formatNotificationTime(
										notification.created_at
									);
									const isSeen = !!notification.viewed_at;

									return (
										<div
											key={notification.id}
											className={`flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
												isSeen ? 'opacity-75' : ''
											}`}
											onClick={() => handleNotificationClick(notification)}>
											<div className='flex-shrink-0'>
												{/* Different dot colors based on notification validity */}
												<div
													className={`h-3 w-3 rounded-full mt-2 ${
														notification.submission_id && notification.form_name
															? isSeen
																? 'bg-gray-300'
																: 'bg-blue-500' // Valid submission
															: notification.metadata?.test_event
															? isSeen
																? 'bg-orange-300'
																: 'bg-orange-500' // Test event
															: isSeen
															? 'bg-gray-300'
															: 'bg-gray-400' // Missing/deleted submission
													}`}></div>
											</div>
											<div className='flex-1 min-w-0'>
												<div className='flex items-start justify-between'>
													<div className='flex-1'>
														<h3
															className={`text-sm font-montserrat-medium ${
																isSeen ? 'text-gray-600' : 'text-gray-900'
															}`}>
															{notification.title}
															{!isSeen && (
																<span className='ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
																	New
																</span>
															)}
														</h3>
														<p
															className={`text-sm mt-1 ${
																isSeen ? 'text-gray-500' : 'text-gray-600'
															}`}>
															{/* Use description if available, otherwise construct from metadata */}
															{notification.description ||
																(notification.metadata?.user_name &&
																notification.metadata?.form_name
																	? `${notification.metadata.user_name} submitted ${notification.metadata.form_name}`
																	: notification.first_name &&
																	  notification.last_name &&
																	  notification.form_name
																	? `${notification.first_name} ${notification.last_name} submitted ${notification.form_name}`
																	: 'Form submission notification')}
														</p>
														{/* Show additional context for missing submissions */}
														{(!notification.submission_id ||
															!notification.form_name) && (
															<p className='text-xs text-gray-400 mt-1'>
																{notification.metadata?.test_event
																	? '(Test notification)'
																	: '(Original submission may have been deleted)'}
															</p>
														)}
														<div className='flex items-center space-x-4 mt-2 text-xs text-gray-500'>
															<span title={timeInfo.absolute}>
																{timeInfo.relative}
															</span>
															{(notification.form_name ||
																notification.metadata?.form_name) && (
																<span className='px-2 py-1 bg-gray-100 rounded-full'>
																	{notification.form_name ||
																		notification.metadata?.form_name}
																</span>
															)}
															{isSeen && notification.viewed_at && (
																<span className='text-gray-400'>
																	Seen{' '}
																	{formatDistanceToNow(
																		new Date(notification.viewed_at),
																		{ addSuffix: true }
																	)}
																</span>
															)}
														</div>
													</div>
													<div className='flex-shrink-0 ml-4'>
														<Button
															variant='ghost'
															size='sm'
															onClick={(e) => {
																e.stopPropagation();
																handleNotificationClick(notification);
															}}
															className='text-gray-400 hover:text-gray-600'>
															<FiEye className='h-4 w-4' />
														</Button>
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>

							{/* Pagination Controls */}
							{totalPages > 1 && (
								<div className='flex items-center justify-between mt-6 pt-6 border-t border-gray-200'>
									<div className='text-sm text-gray-500'>
										Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
										{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of{' '}
										{totalCount} notifications
									</div>
									<div className='flex items-center space-x-2'>
										<Button
											variant='outline'
											size='sm'
											onClick={() => handlePageChange(currentPage - 1)}
											disabled={currentPage === 1 || loading}
											className='flex items-center'>
											<FiChevronLeft className='h-4 w-4 mr-1' />
											Previous
										</Button>

										{/* Page numbers */}
										<div className='flex items-center space-x-1'>
											{Array.from(
												{ length: Math.min(5, totalPages) },
												(_, i) => {
													let pageNum;
													if (totalPages <= 5) {
														pageNum = i + 1;
													} else if (currentPage <= 3) {
														pageNum = i + 1;
													} else if (currentPage >= totalPages - 2) {
														pageNum = totalPages - 4 + i;
													} else {
														pageNum = currentPage - 2 + i;
													}

													return (
														<Button
															key={pageNum}
															variant={
																currentPage === pageNum ? 'primary' : 'outline'
															}
															size='sm'
															onClick={() => handlePageChange(pageNum)}
															disabled={loading}
															className='w-8 h-8 p-0'>
															{pageNum}
														</Button>
													);
												}
											)}
										</div>

										<Button
											variant='outline'
											size='sm'
											onClick={() => handlePageChange(currentPage + 1)}
											disabled={currentPage === totalPages || loading}
											className='flex items-center'>
											Next
											<FiChevronRight className='h-4 w-4 ml-1' />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</Card.Content>
			</Card>
		</div>
	);
};

export default NotificationsPage;
